import type { AyanamsaId, NakshatraPosition, Planet } from '@astrally/shared';
import { getAyanamsa } from '../ayanamsa/lahiri.js';
import type { EphemerisAdapter } from '../ephemeris/adapter.js';
import {
  RAD2DEG,
  angleDiff,
  asinDeg,
  atan2Deg,
  cosDeg,
  norm360,
  sinDeg,
  tanDeg,
} from '../math/angles.js';
import { NAKSHATRA_SPAN, nakshatraOf } from '../core/zodiac.js';
import { tzOffsetMinutes } from '../time/timezone.js';

/**
 * Panchanga — the five limbs (panch + anga) of the Hindu calendar day.
 *
 * Everything here is derived from real Sun and Moon longitudes produced by the
 * ephemeris adapter; nothing is looked up from a table of pre-computed dates.
 *
 *   TITHI      lunar day — every 12° the Moon pulls ahead of the Sun.
 *   VAARA      weekday — but the day flips at SUNRISE, not at midnight.
 *   NAKSHATRA  the lunar mansion (1 of 27) the Moon is sitting in.
 *   YOGA       1 of 27 divisions of (Sun + Moon) longitude.
 *   KARANA     half a tithi — 6° of Sun/Moon separation.
 *
 * Two conventions worth knowing:
 *  - Tithi and karana come from the *difference* of the two longitudes, so
 *    they are identical in the tropical and sidereal zodiacs. Nakshatra and
 *    yoga are sidereal and therefore depend on the chosen ayanamsa.
 *  - The vaara boundary is local sunrise. Get that wrong and every downstream
 *    value (rahu kaal, choghadiya, muhurta ranking) shifts by a whole day.
 */

// ---------------------------------------------------------------------------
// Time / geometry constants
// ---------------------------------------------------------------------------

export const MS_PER_DAY = 86_400_000;
export const MS_PER_MINUTE = 60_000;

/** Standard rise/set altitude: centre of the disc, refraction + semidiameter. */
const RISE_SET_ALTITUDE_DEG = -0.833;

/** Earth's rotation relative to the stars, degrees per mean solar day. */
const DEGREES_PER_SOLAR_DAY = 360.9856473;

/** Tithi span (12°), nakshatra span (13°20'), yoga span (13°20'), karana (6°). */
export const TITHI_SPAN = 12;
export const YOGA_SPAN = 360 / 27;
export const KARANA_SPAN = 6;

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/** The 15 tithi names of a paksha. Index 14 is renamed per paksha (see below). */
export const TITHI_NAMES = [
  'Pratipada',
  'Dwitiya',
  'Tritiya',
  'Chaturthi',
  'Panchami',
  'Shashthi',
  'Saptami',
  'Ashtami',
  'Navami',
  'Dashami',
  'Ekadashi',
  'Dwadashi',
  'Trayodashi',
  'Chaturdashi',
  'Purnima',
] as const;

export const YOGA_NAMES = [
  'Vishkambha',
  'Priti',
  'Ayushman',
  'Saubhagya',
  'Shobhana',
  'Atiganda',
  'Sukarman',
  'Dhriti',
  'Shula',
  'Ganda',
  'Vriddhi',
  'Dhruva',
  'Vyaghata',
  'Harshana',
  'Vajra',
  'Siddhi',
  'Vyatipata',
  'Variyana',
  'Parigha',
  'Shiva',
  'Siddha',
  'Sadhya',
  'Shubha',
  'Shukla',
  'Brahma',
  'Indra',
  'Vaidhriti',
] as const;

/** The two yogas the tradition singles out as unfit for new beginnings. */
export const INAUSPICIOUS_YOGA_INDICES: readonly number[] = [16, 26]; // Vyatipata, Vaidhriti

/** The seven repeating (chara) karanas, in cycle order. */
export const CHARA_KARANA_NAMES = [
  'Bava',
  'Balava',
  'Kaulava',
  'Taitila',
  'Garaja',
  'Vanija',
  'Vishti',
] as const;

/** The four fixed (sthira) karanas that bookend the lunar month. */
export const STHIRA_KARANA_NAMES = ['Shakuni', 'Chatushpada', 'Naga', 'Kimstughna'] as const;

export const VAARA_NAMES = [
  'Ravivara',
  'Somavara',
  'Mangalavara',
  'Budhavara',
  'Guruvara',
  'Shukravara',
  'Shanivara',
] as const;

export const VAARA_ENGLISH = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Weekday lords, 0 = Sunday. */
export const VAARA_LORDS: Planet[] = [
  'sun',
  'moon',
  'mars',
  'mercury',
  'jupiter',
  'venus',
  'saturn',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Paksha = 'shukla' | 'krishna';

export interface PanchangaOptions {
  /**
   * IANA timezone of the place, e.g. "Asia/Kolkata". When omitted the local
   * day is taken from MEAN SOLAR TIME at the given longitude, which is what
   * the classical calendar actually uses and is never more than ~30 minutes
   * from civil time inside a normal timezone.
   */
  timezone?: string;
}

export interface Interval {
  startUtc: Date;
  endUtc: Date;
}

export interface TithiInfo extends Interval {
  /** 1..30 across the whole lunar month. */
  index: number;
  /** 1..15 inside the current paksha. */
  numberInPaksha: number;
  name: string;
  paksha: Paksha;
  /** Elongation Moon − Sun, degrees [0,360). */
  elongationDeg: number;
  /** How far through this tithi we are, 0..1. */
  fraction: number;
}

export interface NakshatraInfo extends Interval, NakshatraPosition {
  /** Sidereal longitude of the Moon at the queried instant. */
  moonLonSidereal: number;
  fraction: number;
}

export interface YogaInfo extends Interval {
  /** 0..26 */
  index: number;
  name: string;
  /** Sidereal Sun + Moon, degrees [0,360). */
  sumDeg: number;
  fraction: number;
  auspicious: boolean;
}

export interface KaranaInfo extends Interval {
  /** 0..59 across the lunar month. */
  index: number;
  name: string;
  kind: 'movable' | 'fixed';
  fraction: number;
  /** Vishti (a.k.a. Bhadra) is the one karana traditionally sat out. */
  isVishti: boolean;
}

export interface VaaraInfo extends Interval {
  /** 0 = Sunday .. 6 = Saturday. */
  index: number;
  name: string;
  englishName: string;
  lord: Planet;
}

export interface SunTimes {
  /** Sunrise opening the local day that contains the queried instant. */
  sunriseUtc: Date;
  sunsetUtc: Date;
  solarNoonUtc: Date;
  dayLengthMs: number;
  /**
   * True when the Sun never rose or never set at this latitude and 06:00 /
   * 18:00 local clock time was substituted. Always surface this to the user
   * rather than presenting a fabricated sunrise as real.
   */
  estimated: boolean;
}

export interface HinduDay extends SunTimes {
  /** Sunrise that CLOSES the day — the Hindu day runs sunrise to sunrise. */
  nextSunriseUtc: Date;
  nightLengthMs: number;
  vaara: VaaraInfo;
}

export interface Panchanga {
  /** The instant the five limbs were read at. */
  utc: Date;
  lat: number;
  lon: number;
  ayanamsaId: AyanamsaId;
  /** Sidereal longitudes actually used, so any reading stays traceable. */
  sunLonSidereal: number;
  moonLonSidereal: number;
  tithi: TithiInfo;
  vaara: VaaraInfo;
  nakshatra: NakshatraInfo;
  yoga: YogaInfo;
  karana: KaranaInfo;
  /** Sunrise/sunset bounding the Hindu day this instant belongs to. */
  day: HinduDay;
}

// ---------------------------------------------------------------------------
// Local-day helpers
// ---------------------------------------------------------------------------

/** Milliseconds to add to a UTC instant to get local wall-clock time. */
function localOffsetMs(utcMs: number, lonEast: number, timezone?: string): number {
  if (timezone) return tzOffsetMinutes(utcMs, timezone) * MS_PER_MINUTE;
  return (lonEast / 15) * 3_600_000;
}

/** Whole days since the Unix epoch, counted in local time. */
function localDayIndex(utcMs: number, lonEast: number, timezone?: string): number {
  return Math.floor((utcMs + localOffsetMs(utcMs, lonEast, timezone)) / MS_PER_DAY);
}

/** Whole days since the Unix epoch at this place — the day-iteration cursor. */
export function localDayIndexFor(utcMs: number, lon: number, timezone?: string): number {
  return localDayIndex(utcMs, lon, timezone);
}

/** Local wall-clock date and time of a UTC instant, for display only. */
export function localTimeParts(
  utcMs: number,
  lon: number,
  timezone?: string,
): { date: string; time: string } {
  const shifted = new Date(utcMs + localOffsetMs(utcMs, lon, timezone));
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`,
    time: `${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`,
  };
}

/** Sunrise/sunset for an explicit local day index (used when scanning a range). */
export function sunTimesForDayIndex(
  dayIndex: number,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  timezone?: string,
): SunTimes {
  return sunTimesForDay(dayIndex, lat, lon, adapter, timezone);
}

/** UTC instant of local clock `hours` on the given local day. */
function localClockToUtcMs(
  dayIndex: number,
  hours: number,
  lonEast: number,
  timezone?: string,
): number {
  const naive = dayIndex * MS_PER_DAY + hours * 3_600_000;
  // Two passes: the offset itself depends on the instant (DST).
  const first = naive - localOffsetMs(naive, lonEast, timezone);
  return naive - localOffsetMs(first, lonEast, timezone);
}

// ---------------------------------------------------------------------------
// Sunrise / sunset from the adapter contract only
// ---------------------------------------------------------------------------

/** Apparent right ascension and declination of the Sun, degrees. */
function sunEquatorial(utc: Date, adapter: EphemerisAdapter): { ra: number; dec: number } {
  const { lon, lat } = adapter.position('sun', utc);
  const eps = adapter.obliquity(utc);
  const ra = atan2Deg(sinDeg(lon) * cosDeg(eps) - tanDeg(lat) * sinDeg(eps), cosDeg(lon));
  const dec = asinDeg(sinDeg(lat) * cosDeg(eps) + cosDeg(lat) * sinDeg(eps) * sinDeg(lon));
  return { ra, dec };
}

/** Local hour angle of a body, degrees in (-180, 180]. Negative = still rising. */
function hourAngleDeg(utc: Date, lonEast: number, ra: number, adapter: EphemerisAdapter): number {
  const gastDeg = adapter.siderealTimeHours(utc) * 15;
  const h = norm360(gastDeg + lonEast - ra);
  return h > 180 ? h - 360 : h;
}

/** Refine an approximate local solar noon to the instant of true transit. */
function refineTransit(seedMs: number, lonEast: number, adapter: EphemerisAdapter): number {
  let t = seedMs;
  for (let i = 0; i < 3; i++) {
    const at = new Date(t);
    const { ra } = sunEquatorial(at, adapter);
    const h = hourAngleDeg(at, lonEast, ra, adapter);
    t -= (h / DEGREES_PER_SOLAR_DAY) * MS_PER_DAY;
  }
  return t;
}

/**
 * Refine a rise/set estimate with Meeus' correction
 * Δm = (h − h0) / (360 · cos δ · cos φ · sin H).
 */
function refineRiseSet(
  seedMs: number,
  lat: number,
  lonEast: number,
  adapter: EphemerisAdapter,
): number | null {
  let t = seedMs;
  for (let i = 0; i < 4; i++) {
    const at = new Date(t);
    const { ra, dec } = sunEquatorial(at, adapter);
    const h = hourAngleDeg(at, lonEast, ra, adapter);
    const alt = asinDeg(sinDeg(lat) * sinDeg(dec) + cosDeg(lat) * cosDeg(dec) * cosDeg(h));
    const denom = 360 * cosDeg(dec) * cosDeg(lat) * sinDeg(h);
    if (!Number.isFinite(denom) || Math.abs(denom) < 1e-8) return null;
    const delta = ((alt - RISE_SET_ALTITUDE_DEG) / denom) * MS_PER_DAY;
    t += delta;
    if (Math.abs(delta) < 20) break; // 20 ms — well past useful precision
  }
  return Number.isFinite(t) ? t : null;
}

/** Sunrise/sunset for one local day index. Never throws; flags polar days. */
function sunTimesForDay(
  dayIndex: number,
  lat: number,
  lonEast: number,
  adapter: EphemerisAdapter,
  timezone?: string,
): SunTimes {
  const noonSeed = localClockToUtcMs(dayIndex, 12, lonEast, timezone);
  const transit = refineTransit(noonSeed, lonEast, adapter);
  const { dec } = sunEquatorial(new Date(transit), adapter);

  const cosH0 =
    (sinDeg(RISE_SET_ALTITUDE_DEG) - sinDeg(lat) * sinDeg(dec)) / (cosDeg(lat) * cosDeg(dec));

  let riseMs: number | null = null;
  let setMs: number | null = null;
  if (Number.isFinite(cosH0) && cosH0 <= 1 && cosH0 >= -1) {
    const h0 = Math.acos(cosH0) * RAD2DEG;
    const halfDay = (h0 / DEGREES_PER_SOLAR_DAY) * MS_PER_DAY;
    riseMs = refineRiseSet(transit - halfDay, lat, lonEast, adapter);
    setMs = refineRiseSet(transit + halfDay, lat, lonEast, adapter);
  }

  // Polar day/night (or a degenerate solve): substitute clock times and say so.
  const estimated = riseMs === null || setMs === null;
  const sunriseMs = riseMs ?? localClockToUtcMs(dayIndex, 6, lonEast, timezone);
  const sunsetMs = setMs ?? localClockToUtcMs(dayIndex, 18, lonEast, timezone);

  return {
    sunriseUtc: new Date(sunriseMs),
    sunsetUtc: new Date(sunsetMs),
    solarNoonUtc: new Date(transit),
    dayLengthMs: sunsetMs - sunriseMs,
    estimated,
  };
}

/**
 * Sunrise and sunset for the local calendar day containing `utc`.
 * Exported because muhurta windows and the vaara boundary both need it.
 */
export function computeSunTimes(
  utc: Date,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  options: PanchangaOptions = {},
): SunTimes {
  const dayIndex = localDayIndex(utc.getTime(), lon, options.timezone);
  return sunTimesForDay(dayIndex, lat, lon, adapter, options.timezone);
}

/** The weekday that a given sunrise opens. */
export function vaaraFor(
  sunriseUtc: Date,
  nextSunriseUtc: Date,
  lon: number,
  timezone?: string,
): VaaraInfo {
  const ms = sunriseUtc.getTime();
  const index = new Date(ms + localOffsetMs(ms, lon, timezone)).getUTCDay();
  return {
    index,
    name: VAARA_NAMES[index],
    englishName: VAARA_ENGLISH[index],
    lord: VAARA_LORDS[index],
    startUtc: sunriseUtc,
    endUtc: nextSunriseUtc,
  };
}

/**
 * The Hindu day containing `utc`: sunrise → next sunrise, with the vaara that
 * sunrise names. An instant between midnight and sunrise still belongs to the
 * PREVIOUS weekday's day — that is the whole point of the sunrise boundary.
 */
export function computeHinduDay(
  utc: Date,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  options: PanchangaOptions = {},
): HinduDay {
  const tz = options.timezone;
  let dayIndex = localDayIndex(utc.getTime(), lon, tz);
  let today = sunTimesForDay(dayIndex, lat, lon, adapter, tz);

  // Before today's sunrise → the running Hindu day started yesterday.
  if (utc.getTime() < today.sunriseUtc.getTime()) {
    dayIndex -= 1;
    today = sunTimesForDay(dayIndex, lat, lon, adapter, tz);
  }
  let next = sunTimesForDay(dayIndex + 1, lat, lon, adapter, tz);
  // Defensive: keep advancing if the instant is past the next sunrise.
  let guard = 0;
  while (utc.getTime() >= next.sunriseUtc.getTime() && guard < 3) {
    dayIndex += 1;
    today = next;
    next = sunTimesForDay(dayIndex + 1, lat, lon, adapter, tz);
    guard += 1;
  }

  return {
    ...today,
    nextSunriseUtc: next.sunriseUtc,
    nightLengthMs: next.sunriseUtc.getTime() - today.sunsetUtc.getTime(),
    vaara: vaaraFor(today.sunriseUtc, next.sunriseUtc, lon, tz),
  };
}

/** The Hindu day opened by the sunrise of an explicit local day index. */
export function hinduDayForIndex(
  dayIndex: number,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  timezone?: string,
): HinduDay {
  const today = sunTimesForDay(dayIndex, lat, lon, adapter, timezone);
  const next = sunTimesForDay(dayIndex + 1, lat, lon, adapter, timezone);
  return {
    ...today,
    nextSunriseUtc: next.sunriseUtc,
    nightLengthMs: next.sunriseUtc.getTime() - today.sunsetUtc.getTime(),
    vaara: vaaraFor(today.sunriseUtc, next.sunriseUtc, lon, timezone),
  };
}

// ---------------------------------------------------------------------------
// Sun / Moon longitudes and boundary solving
// ---------------------------------------------------------------------------

interface LuminaryState {
  sunLon: number;
  moonLon: number;
  sunSpeed: number;
  moonSpeed: number;
}

function luminaries(utc: Date, adapter: EphemerisAdapter, ayanamsaId: AyanamsaId): LuminaryState {
  const ayan = getAyanamsa(ayanamsaId).valueDeg(adapter.julianCenturiesTT(utc));
  const sun = adapter.position('sun', utc);
  const moon = adapter.position('moon', utc);
  return {
    sunLon: norm360(sun.lon - ayan),
    moonLon: norm360(moon.lon - ayan),
    sunSpeed: sun.speed,
    moonSpeed: moon.speed,
  };
}

/** Mean daily motion used as a fallback when an adapter reports no speed. */
const FALLBACK_RATES = { elongation: 12.19, moon: 13.176, sum: 14.16 };

type AngleKind = 'elongation' | 'moon' | 'sum';

function angleValue(state: LuminaryState, kind: AngleKind): number {
  if (kind === 'elongation') return norm360(state.moonLon - state.sunLon);
  if (kind === 'moon') return state.moonLon;
  return norm360(state.sunLon + state.moonLon);
}

function angleRate(state: LuminaryState, kind: AngleKind): number {
  if (kind === 'elongation') return state.moonSpeed - state.sunSpeed;
  if (kind === 'moon') return state.moonSpeed;
  return state.sunSpeed + state.moonSpeed;
}

/**
 * Newton solve for the instant an angle reaches `targetDeg`.
 * The seed is always within one division of the target, so the signed
 * shortest-arc difference is unambiguous and convergence takes 3-4 steps.
 */
function solveAngleCrossing(
  kind: AngleKind,
  targetDeg: number,
  seedMs: number,
  adapter: EphemerisAdapter,
  ayanamsaId: AyanamsaId,
): Date {
  let t = seedMs;
  for (let i = 0; i < 8; i++) {
    const state = luminaries(new Date(t), adapter, ayanamsaId);
    const diff = angleDiff(targetDeg, angleValue(state, kind));
    if (Math.abs(diff) < 1e-7) break;
    const rate = angleRate(state, kind);
    const safeRate = Math.abs(rate) > 1 ? rate : FALLBACK_RATES[kind];
    t += (diff / safeRate) * MS_PER_DAY;
  }
  return new Date(t);
}

function bounds(
  kind: AngleKind,
  span: number,
  divisionIndex: number,
  seedMs: number,
  adapter: EphemerisAdapter,
  ayanamsaId: AyanamsaId,
): Interval {
  return {
    startUtc: solveAngleCrossing(kind, norm360(divisionIndex * span), seedMs, adapter, ayanamsaId),
    endUtc: solveAngleCrossing(kind, norm360((divisionIndex + 1) * span), seedMs, adapter, ayanamsaId),
  };
}

// ---------------------------------------------------------------------------
// The five limbs
// ---------------------------------------------------------------------------

export function tithiName(index: number): { name: string; paksha: Paksha; numberInPaksha: number } {
  const i = ((index - 1) % 30 + 30) % 30; // 0..29
  const paksha: Paksha = i < 15 ? 'shukla' : 'krishna';
  const numberInPaksha = (i % 15) + 1;
  let name: string = TITHI_NAMES[numberInPaksha - 1];
  if (numberInPaksha === 15) name = paksha === 'shukla' ? 'Purnima' : 'Amavasya';
  return { name, paksha, numberInPaksha };
}

/**
 * Karana name for a half-tithi index 0..59.
 * The month opens with the fixed Kimstughna, then the seven movable karanas
 * repeat eight times (indices 1..56), then the fixed Shakuni, Chatushpada and
 * Naga close it out.
 */
export function karanaName(index: number): { name: string; kind: 'movable' | 'fixed' } {
  const i = ((index % 60) + 60) % 60;
  if (i === 0) return { name: 'Kimstughna', kind: 'fixed' };
  if (i === 57) return { name: 'Shakuni', kind: 'fixed' };
  if (i === 58) return { name: 'Chatushpada', kind: 'fixed' };
  if (i === 59) return { name: 'Naga', kind: 'fixed' };
  return { name: CHARA_KARANA_NAMES[(i - 1) % 7], kind: 'movable' };
}

/**
 * All five limbs at one instant, plus the sunrise/sunset day they sit in.
 *
 * `utc` is a real instant; `lat`/`lon` are the place the day is being read
 * for (sunrise, and therefore the vaara, is location-dependent).
 */
export function computePanchanga(
  utc: Date,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
  ayanamsaId: AyanamsaId = 'lahiri',
  options: PanchangaOptions = {},
): Panchanga {
  const seed = utc.getTime();
  const state = luminaries(utc, adapter, ayanamsaId);

  // --- Tithi -------------------------------------------------------------
  const elongation = norm360(state.moonLon - state.sunLon);
  const tithiDivision = Math.floor(elongation / TITHI_SPAN); // 0..29
  const tithiIndex = tithiDivision + 1; // 1..30
  const naming = tithiName(tithiIndex);
  const tithiBounds = bounds('elongation', TITHI_SPAN, tithiDivision, seed, adapter, ayanamsaId);
  const tithi: TithiInfo = {
    index: tithiIndex,
    numberInPaksha: naming.numberInPaksha,
    name: naming.name,
    paksha: naming.paksha,
    elongationDeg: elongation,
    fraction: (elongation - tithiDivision * TITHI_SPAN) / TITHI_SPAN,
    ...tithiBounds,
  };

  // --- Nakshatra ---------------------------------------------------------
  const nak = nakshatraOf(state.moonLon);
  const nakBounds = bounds('moon', NAKSHATRA_SPAN, nak.index, seed, adapter, ayanamsaId);
  const nakshatra: NakshatraInfo = {
    ...nak,
    moonLonSidereal: state.moonLon,
    fraction: (state.moonLon - nak.index * NAKSHATRA_SPAN) / NAKSHATRA_SPAN,
    ...nakBounds,
  };

  // --- Yoga --------------------------------------------------------------
  const sum = norm360(state.sunLon + state.moonLon);
  const yogaIndex = Math.floor(sum / YOGA_SPAN) % 27;
  const yogaBounds = bounds('sum', YOGA_SPAN, yogaIndex, seed, adapter, ayanamsaId);
  const yoga: YogaInfo = {
    index: yogaIndex,
    name: YOGA_NAMES[yogaIndex],
    sumDeg: sum,
    fraction: (sum - yogaIndex * YOGA_SPAN) / YOGA_SPAN,
    auspicious: !INAUSPICIOUS_YOGA_INDICES.includes(yogaIndex),
    ...yogaBounds,
  };

  // --- Karana ------------------------------------------------------------
  const karanaIndex = Math.floor(elongation / KARANA_SPAN); // 0..59
  const kName = karanaName(karanaIndex);
  const karanaBounds = bounds('elongation', KARANA_SPAN, karanaIndex, seed, adapter, ayanamsaId);
  const karana: KaranaInfo = {
    index: karanaIndex,
    name: kName.name,
    kind: kName.kind,
    fraction: (elongation - karanaIndex * KARANA_SPAN) / KARANA_SPAN,
    isVishti: kName.name === 'Vishti',
    ...karanaBounds,
  };

  // --- Vaara (sunrise-bounded) -------------------------------------------
  const day = computeHinduDay(utc, lat, lon, adapter, options);

  return {
    utc,
    lat,
    lon,
    ayanamsaId,
    sunLonSidereal: state.sunLon,
    moonLonSidereal: state.moonLon,
    tithi,
    vaara: day.vaara,
    nakshatra,
    yoga,
    karana,
    day,
  };
}

/**
 * Indices only — no boundary solving. Used by the muhurta ranker, which reads
 * the limbs at dozens of candidate instants and never needs their end times.
 */
export interface PanchangaLimbIndices {
  tithi: number;
  paksha: Paksha;
  tithiName: string;
  nakshatra: number;
  nakshatraName: string;
  nakshatraLord: Planet;
  yoga: number;
  yogaName: string;
  karana: number;
  karanaName: string;
  isVishti: boolean;
}

export function panchangaLimbsAt(
  utc: Date,
  adapter: EphemerisAdapter,
  ayanamsaId: AyanamsaId = 'lahiri',
): PanchangaLimbIndices {
  const state = luminaries(utc, adapter, ayanamsaId);
  const elongation = norm360(state.moonLon - state.sunLon);
  const tithiIndex = Math.floor(elongation / TITHI_SPAN) + 1;
  const naming = tithiName(tithiIndex);
  const nak = nakshatraOf(state.moonLon);
  const yogaIndex = Math.floor(norm360(state.sunLon + state.moonLon) / YOGA_SPAN) % 27;
  const karanaIndex = Math.floor(elongation / KARANA_SPAN);
  const kName = karanaName(karanaIndex);
  return {
    tithi: tithiIndex,
    paksha: naming.paksha,
    tithiName: naming.name,
    nakshatra: nak.index,
    nakshatraName: nak.name,
    nakshatraLord: nak.lord,
    yoga: yogaIndex,
    yogaName: YOGA_NAMES[yogaIndex],
    karana: karanaIndex,
    karanaName: kName.name,
    isVishti: kName.name === 'Vishti',
  };
}
